import React, { useState } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ConfirmModal } from './ConfirmModal';
import { ChevronDown, ChevronUp, GitBranch } from 'lucide-react';

type TriState = 'unchanged' | 'true' | 'false';

const FLAG_FIELDS: { key: 'is_online_booking_active' | 'is_online_store_active' | 'is_scan_and_go_active'; label: string }[] = [
  { key: 'is_online_booking_active', label: 'Online Booking' },
  { key: 'is_online_store_active', label: 'Online Store' },
  { key: 'is_scan_and_go_active', label: 'Scan-and-Go' },
];

function TriToggle({ value, onChange, label }: { value: TriState; onChange: (v: TriState) => void; label: string }) {
  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border dark:border-slate-700">
      <span className="text-sm font-medium dark:text-slate-200">{label}</span>
      <div className="flex bg-white dark:bg-slate-900 rounded-md border dark:border-slate-700 overflow-hidden">
        {(['unchanged', 'true', 'false'] as TriState[]).map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              value === opt
                ? opt === 'true'
                  ? 'bg-emerald-500 text-white'
                  : opt === 'false'
                  ? 'bg-red-500 text-white'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
                : 'text-slate-500 dark:text-slate-400'
            }`}
          >
            {opt === 'unchanged' ? "Don't change" : opt === 'true' ? 'ON' : 'OFF'}
          </button>
        ))}
      </div>
    </div>
  );
}

export function BranchBulkActionPanel() {
  const [expanded, setExpanded] = useState(false);
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [status, setStatus] = useState<TriState>('unchanged');
  const [flags, setFlags] = useState<Record<string, TriState>>({
    is_online_booking_active: 'unchanged',
    is_online_store_active: 'unchanged',
    is_scan_and_go_active: 'unchanged',
  });
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const hasScope = city.trim() !== '' || pincode.trim() !== '';
  const hasAnyChange = status !== 'unchanged' || Object.values(flags).some((v) => v !== 'unchanged');

  const triToBool = (v: TriState): boolean | null => (v === 'unchanged' ? null : v === 'true');

  const handleApply = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setApplying(true);

    const { data, error } = await supabase.rpc('admin_bulk_set_branch_flags', {
      p_city: city.trim() || null,
      p_pincode: pincode.trim() || null,
      p_status: triToBool(status) === null ? null : status === 'true' ? 'active' : 'inactive',
      p_is_online_booking_active: triToBool(flags.is_online_booking_active),
      p_is_online_store_active: triToBool(flags.is_online_store_active),
      p_is_scan_and_go_active: triToBool(flags.is_scan_and_go_active),
      p_reason: reason.trim() || null,
    });

    if (error) {
      toast.error(error.message || 'Bulk update failed');
    } else {
      toast.success(`${data ?? 0} branches affected`);
      setConfirmOpen(false);
      setStatus('unchanged');
      setFlags({ is_online_booking_active: 'unchanged', is_online_store_active: 'unchanged', is_scan_and_go_active: 'unchanged' });
      setReason('');
    }
    setApplying(false);
  };

  return (
    <Card className="dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <CardTitle className="flex items-center gap-2 text-base dark:text-white">
          <GitBranch size={18} /> Bulk Branch Action (by City / Pincode)
        </CardTitle>
        {expanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
      </button>

      {expanded && (
        <CardContent className="space-y-4 border-t dark:border-slate-800 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium dark:text-slate-300">City</label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Bahraich" className="dark:bg-slate-800 dark:border-slate-700 dark:text-white" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium dark:text-slate-300">Pincode</label>
              <Input value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="e.g. 271801" className="dark:bg-slate-800 dark:border-slate-700 dark:text-white" />
            </div>
          </div>
          {!hasScope && (
            <p className="text-xs text-amber-600 dark:text-amber-400">City ya Pincode mein se kam se kam ek daalein.</p>
          )}

          <TriToggle value={status} onChange={setStatus} label="Branch Status" />
          {FLAG_FIELDS.map((f) => (
            <TriToggle key={f.key} value={flags[f.key]} onChange={(v) => setFlags((prev) => ({ ...prev, [f.key]: v }))} label={f.label} />
          ))}

          <div className="space-y-1.5">
            <label className="text-sm font-medium dark:text-slate-300">Reason</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Kyun kiya jaa raha hai yeh change?" className="dark:bg-slate-800 dark:border-slate-700 dark:text-white" />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!hasScope || !hasAnyChange}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              Apply
            </Button>
          </div>
        </CardContent>
      )}

      <ConfirmModal
        open={confirmOpen}
        title="Bulk branch change confirm karein?"
        description={
          <>
            Yeh {city.trim() && <b>City: {city}</b>} {city.trim() && pincode.trim() && ' & '} {pincode.trim() && <b>Pincode: {pincode}</b>} ki saari branches ko affect karega.
          </>
        }
        confirmLabel="Haan, Apply Karein"
        loading={applying}
        onConfirm={handleApply}
        onCancel={() => setConfirmOpen(false)}
      />
    </Card>
  );
}

export default BranchBulkActionPanel;
